import React, { useCallback, useMemo, useState } from 'react';
import { SortBy } from 'spw-components/collab/types';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectProjectsForSelectedUserList,
  selectSelectedUser,
} from 'spw-redux/modules/users/selectors';
import { InfoItemVM } from 'spw-components/collab/info-button';
import TransferProjectOwnershipDialog from 'spw-projects/transfer-project-ownership-dialog';
import {
  changeProjectOwner,
  getProjectsForSelectedUser,
  refetchProjectsForSelectedUserStartingFromPage,
  setUserFavoriteProject,
} from 'spw-redux/modules/users/actions';
import { OnListEndReachedCallback } from 'spw-shared/hooks/use-on-scrolled-until-bottom';
import ActionsDialog, {
  IActionsDialog,
} from 'spw-components/dialogs/actions-dialog';
import { removeProjectEditor } from 'spw-redux/modules/projects/actions';
import {
  ProjectListItemVM,
  ProjectsSortingParams,
} from 'spw-redux/modules/projects/types';
import LeaveProjectDialog from 'spw-projects/leave-project-dialog';
import ProjectsList, { ProjectVM } from 'spw-projects/projects-list';
import { currentUser as selectCurrentUser } from 'spw-redux/modules/user/selectors';
import { IUser } from 'spw-redux/modules/user/types';
import { getActionForUserProject, UserProjectAction } from './permissions';

export const USER_PROJECT_ITEMS_PER_PAGE = 30;

const AVAILABLE_INFO_ITEMS_MAP: { [key in UserProjectAction]: InfoItemVM } = {
  [UserProjectAction.remove]: {
    title: 'REMOVE USER FROM THE PROJECT',
    content: 'The user loses edit access when they are removed from a project.',
  },
  [UserProjectAction.transfer]: {
    title: 'TRANSFER PROJECT OWNERSHIP',
    content:
      'The project can be transferred to any person in the organization.',
  },
  [UserProjectAction.reassign]: {
    title: 'REASSIGN PROJECT OWNER',
    content:
      'The project can be transferred to any person in the organization.',
  },
  [UserProjectAction.leave]: {
    title: 'LEAVE A PROJECT',
    content: 'You lose edit access to projects that you leave.',
  },
};

const PROJECTS_TABLE_DISABLED_COLUMNS = { lastModified: true };

const mapFiltersToAPIParams = (
  userId: number,
  sortBy: SortBy<keyof ProjectsSortingParams>,
  page?: number
) => {
  return {
    userId,
    sort: {
      [sortBy.column]: sortBy.direction,
    },
    page,
  };
};

const UserProjectsTable = () => {
  const dispatch = useDispatch();
  const projectsList = useSelector(selectProjectsForSelectedUserList);
  const selectedUser = useSelector(selectSelectedUser);
  const currentUser = useSelector(selectCurrentUser);
  const [leaveProjectDialogState, setLeaveProjectDialogState] = useState<{
    isVisible: boolean;
    project: ProjectVM;
  }>({
    isVisible: false,
    project: null,
  });
  const [actionsDialogState, setActionsDialogState] = useState<IActionsDialog>({
    isOpen: false,
  });
  const [
    transferProjectOwnershipDialogState,
    setTransferProjectOwnershipDialogState,
  ] = useState<{
    isVisible: boolean;
    project?: ProjectListItemVM;
  }>({
    isVisible: false,
    project: null,
  });
  const [sortBy, setSortBy] = useState<SortBy<keyof ProjectsSortingParams>>({
    column: 'isMarkedAsFavoriteByUser',
    direction: 'ASC',
  });

  const { projectsToRender, infoItems } = useMemo(() => {
    const actionsInListMap: { [key: string]: InfoItemVM } = {};
    return {
      projectsToRender: projectsList.items.map((p) => {
        const action = getActionForUserProject(selectedUser, currentUser, p);
        if (action) actionsInListMap[action] = AVAILABLE_INFO_ITEMS_MAP[action];
        return {
          ...p,
          action,
        } as ProjectVM;
      }),
      infoItems: Object.values(actionsInListMap),
    };
  }, [projectsList.items, selectedUser, currentUser]);

  const handleTransferOwnershipSuccess = (
    projectId: number,
    preservePreviousOwnerAsEditor: boolean,
    newProjectOwner: IUser
  ) => {
    if (preservePreviousOwnerAsEditor) {
      dispatch(changeProjectOwner(projectId, newProjectOwner));
    } else {
      const index = projectsList.items.findIndex(({ id }) => id === projectId);

      dispatch(
        refetchProjectsForSelectedUserStartingFromPage({
          userId: selectedUser?.id,
          startingPage: Math.floor(index / USER_PROJECT_ITEMS_PER_PAGE) + 1,
        })
      );
    }
  };

  const handleLeaveProjectSuccess = (projectId: number) => {
    const index = projectsList.items.findIndex(({ id }) => id === projectId);

    dispatch(
      refetchProjectsForSelectedUserStartingFromPage({
        userId: selectedUser?.id,
        startingPage: Math.floor(index / USER_PROJECT_ITEMS_PER_PAGE) + 1,
      })
    );
  };

  const sortRows = useCallback(
    (newSortBy: SortBy<keyof ProjectsSortingParams>) => {
      setSortBy(newSortBy);
      dispatch(
        getProjectsForSelectedUser(
          mapFiltersToAPIParams(selectedUser.id, newSortBy)
        )
      );
    },
    [dispatch, selectedUser?.id]
  );

  const handleCloseActionsDialog = useCallback(
    () => setActionsDialogState((prev) => ({ ...prev, isOpen: false })),
    []
  );

  const handleRemovedFromProjectSuccessfully = useCallback(() => {
    dispatch(getProjectsForSelectedUser({ userId: selectedUser?.id }));
  }, [dispatch, selectedUser?.id]);

  const handleListEndReached: OnListEndReachedCallback = ({
    isLastPage,
    nextPage,
  }) => {
    if (!isLastPage)
      dispatch(
        getProjectsForSelectedUser(
          mapFiltersToAPIParams(selectedUser.id, sortBy, nextPage)
        )
      );
  };

  const removeSelectedUserFromProject = useCallback(
    (project: ProjectListItemVM) => {
      dispatch(
        removeProjectEditor(
          selectedUser?.id,
          project.id,
          handleRemovedFromProjectSuccessfully
        )
      );
      handleCloseActionsDialog();
    },
    [
      selectedUser,
      dispatch,
      handleRemovedFromProjectSuccessfully,
      handleCloseActionsDialog,
    ]
  );

  const handleProjectAction = useCallback(
    (project: ProjectListItemVM, action: UserProjectAction) => {
      if (
        action === UserProjectAction.transfer ||
        action === UserProjectAction.reassign
      ) {
        setTransferProjectOwnershipDialogState({ isVisible: true, project });
      } else if (action === UserProjectAction.leave) {
        setLeaveProjectDialogState({ isVisible: true, project });
      } else if (action === UserProjectAction.remove) {
        setActionsDialogState({
          isOpen: true,
          onClose: handleCloseActionsDialog,
          title: 'Remove From Project',
          description:
            'Removing a project removes edit access to the project. The user will keep ownership of the projects that they created.',
          actions: [
            {
              text: 'REMOVE',
              onClick: () => removeSelectedUserFromProject(project),
              variant: 'delete',
            },
            {
              text: 'CANCEL',
              onClick: handleCloseActionsDialog,
              variant: 'cancel',
            },
          ],
        });
      }
    },
    [handleCloseActionsDialog, removeSelectedUserFromProject]
  );

  return (
    <>
      <ProjectsList
        projects={projectsToRender}
        onProjectActionClick={handleProjectAction}
        infoItems={infoItems}
        itemsPerPage={USER_PROJECT_ITEMS_PER_PAGE}
        onScrolledUntilBottom={handleListEndReached}
        onSortChange={sortRows}
        sortBy={sortBy}
        disabledColumns={{
          isMarkedAsFavoriteByUser: selectedUser.id !== currentUser.id,
          ...PROJECTS_TABLE_DISABLED_COLUMNS,
        }}
        disableCloning
        onToggledFavorite={(projectId, newValue) => {
          dispatch(setUserFavoriteProject(projectId, newValue));
        }}
      />
      <TransferProjectOwnershipDialog
        {...transferProjectOwnershipDialogState}
        onClose={() =>
          setTransferProjectOwnershipDialogState({ isVisible: false })
        }
        onTransferSuccess={handleTransferOwnershipSuccess}
      />
      <LeaveProjectDialog
        project={leaveProjectDialogState.project}
        isVisible={leaveProjectDialogState.isVisible}
        onLeaveProjectSuccess={handleLeaveProjectSuccess}
        onClose={() =>
          setLeaveProjectDialogState({ isVisible: false, project: null })
        }
      />
      <ActionsDialog {...actionsDialogState} />
    </>
  );
};

export default UserProjectsTable;
